import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPaymentStatus } from '@/modules/templates/services/guest-payment.service';

// Public landing page for Adyen 3DS / redirect-result returns.
//
// We don't reconstruct the AdyenCheckout instance here (the original Drop-in
// session lives in the page that initiated payment, but is single-use after
// redirect). Instead we just poll our backend for paid status — the webhook
// is the source of truth, and the redirect arriving here means Adyen is
// done with the user's part. If they paid successfully, the webhook will
// flip IsPaid within seconds.

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

const PaymentResultPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const documentId = searchParams.get('documentId');
  const [status, setStatus] = useState<'polling' | 'success' | 'timeout' | 'no-doc'>(
    documentId ? 'polling' : 'no-doc',
  );

  useEffect(() => {
    if (!documentId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();

    const poll = async () => {
      if (cancelled) return;
      try {
        const result = await getPaymentStatus(documentId);
        if (cancelled) return;
        if (result.isPaid) {
          setStatus('success');
          // Brief pause so the user sees "success" before redirect.
          setTimeout(() => navigate(-1), 1500);
          return;
        }
      } catch (err) {
        console.warn('[PaymentResult] poll error', err);
      }
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        setStatus('timeout');
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    timer = setTimeout(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [documentId, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg p-6 text-center">
        {status === 'no-doc' && (
          <>
            <h1 className="text-lg font-semibold mb-2">Missing document reference</h1>
            <p className="text-sm text-globe-grey">
              This page expects a documentId in the URL. Return to your document and try again.
            </p>
          </>
        )}
        {status === 'polling' && (
          <>
            <h1 className="text-lg font-semibold mb-2">Confirming your payment…</h1>
            <p className="text-sm text-globe-grey">
              This usually takes just a few seconds.
            </p>
          </>
        )}
        {status === 'success' && (
          <>
            <h1 className="text-lg font-semibold mb-2 text-emerald-600">
              Payment confirmed
            </h1>
            <p className="text-sm text-globe-grey">
              Returning you to your document…
            </p>
          </>
        )}
        {status === 'timeout' && (
          <>
            <h1 className="text-lg font-semibold mb-2">Still processing</h1>
            <p className="text-sm text-globe-grey">
              Refresh in a minute. If the document remains locked, try paying again.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentResultPage;
